export const WATCHABLE_ENTITY_TYPES = [
  'loop',
  'team',
  'objective',
  'item',
  'demand',
  'idea',
] as const;

export type WatchableEntityType = (typeof WATCHABLE_ENTITY_TYPES)[number];

// The backend services that own at least one watchable entity type. Kept as its own literal
// union (not plain `string`) so a typo or a future service rename fails to compile here, rather
// than silently drifting — this registry exists specifically to prevent that kind of drift.
export const OWNING_SERVICES = [
  'item-store',
  'capacity',
  'reporting-store',
  'demand',
  'fim',
] as const;
export type OwningService = (typeof OWNING_SERVICES)[number];

// Shared gRPC client packages available today for a watchable entity's owning service. Not every
// owning service has one yet (see demand.ts / fim.ts) — `clientPackage` is nullable for those.
export const ENTITY_CLIENT_PACKAGES = [
  '@tempo-io/backend-item-store-client',
  '@tempo-io/backend-capacity-client',
  '@tempo-io/backend-reporting-store-client',
] as const;
export type EntityClientPackage = (typeof ENTITY_CLIENT_PACKAGES)[number];

export interface EntityLinkContext {
  organizationId: string;
  loopId?: number;
}

export type BatchLabelResolver = (
  entityIds: readonly string[],
  ctx: EntityLinkContext,
) => Promise<ReadonlyMap<string, string>>;

export interface EntityOwnerDescriptor<T extends WatchableEntityType> {
  entityType: T;
  owningService: OwningService;
  clientPackage: EntityClientPackage | null;
  cdcTopic: string | null;
  resolveLink: (entityId: string, ctx: EntityLinkContext) => string;
  resolveLabels: BatchLabelResolver | null;
}

export const defineEntityOwner = <T extends WatchableEntityType>(
  entityType: T,
  descriptor: Omit<EntityOwnerDescriptor<T>, 'entityType'>,
): EntityOwnerDescriptor<T> => ({ entityType, ...descriptor });
